import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight, Eye, Loader2, UserRound, Wrench } from 'lucide-react';
import {
  calendarApi, KINDS, KIND_BY_KEY, colourOf, isoToday, addDays, addMonths, monthStart, monthEnd, weekStart, DOW, fmt, timeOf, minutesOf, daysOf, browserTz,
  type CalEvent, type EventKind, type ManualKind,
} from '../lib/calendar';
import { usePropertyOptions, useUnitOptions } from '../lib/options';
import { Segmented, PageHeader, Button, Modal, Field, Input, Select, Textarea, EmptyState } from '../components/ui';
import { StatTile } from '../components/charts/StatTile';
import { FormError } from '../components/forms/common';
import { useAuth } from '../stores/auth';


type View = 'month' | 'week' | 'day';
const DAY_START = 7;   // the time grid shows 07:00 …
const DAY_END = 21;    // … to 21:00; earlier / later events sit at the edge
const HOUR_PX = 48;

function useNarrow() {
  const q = '(max-width: 639px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

/**
 * One diary for the whole company (prototype §31). Work orders booked in, viewings,
 * inspections, servicing, certificate renewals and move-ins / move-outs are read off
 * their own records; anything with no record of its own is booked here by hand.
 */
export default function CalendarPage() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const narrow = useNarrow();
  const [view, setView] = useState<View>('month');
  const [anchor, setAnchor] = useState(isoToday());
  const [staffId, setStaffId] = useState('');
  const [kind, setKind] = useState<EventKind | ''>('');
  const [propertyId, setPropertyId] = useState('');
  const [editing, setEditing] = useState<CalEvent | null>(null);
  const [booking, setBooking] = useState<string | null>(null);   // the day to book on
  const [viewingManual, setViewingManual] = useState<CalEvent | null>(null);
  const canBook = can('calendar.create');
  const today = isoToday();

  // The range on screen: a 6-week month grid (or the month itself as an agenda on a phone), a week, a day.
  const [from, to] = useMemo((): [string, string] => {
    if (view === 'day') return [anchor, anchor];
    if (view === 'week') { const s = weekStart(anchor); return [s, addDays(s, 6)]; }
    if (narrow) return [monthStart(anchor), monthEnd(anchor)];
    const s = weekStart(monthStart(anchor));
    return [s, addDays(s, 41)];
  }, [view, anchor, narrow]);

  const q = useQuery({
    queryKey: ['/calendar', { from, to, staffId, kind, propertyId }],
    queryFn: () => calendarApi.list({ from, to, staffId: staffId || undefined, kind: kind || undefined, propertyId: propertyId || undefined }),
    placeholderData: (prev) => prev,
  });
  const people = useQuery({ queryKey: ['/calendar/people'], queryFn: calendarApi.people, enabled: canBook, staleTime: 60_000 });
  const properties = usePropertyOptions();
  const events = q.data?.events ?? [];
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/calendar'] }); };

  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of events) for (const d of daysOf(e, from, to)) m.set(d, [...(m.get(d) ?? []), e]);
    return m;
  }, [events, from, to]);

  const open = (e: CalEvent) => {
    if (e.source === 'manual') return e.editable ? setEditing(e) : setViewingManual(e);
    if (e.url) navigate(e.url);
  };
  const step = (n: number) => setAnchor((a) => (view === 'month' ? addMonths(a, n) : addDays(a, n * (view === 'week' ? 7 : 1))));
  const title = view === 'month' ? fmt(monthStart(anchor), { month: 'long', year: 'numeric' })
    : view === 'week' ? `${fmt(from, { day: 'numeric', month: 'short' })} – ${fmt(to, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : fmt(anchor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const inMonth = view === 'month' && !narrow ? events.filter((e) => e.day.slice(0, 7) === anchor.slice(0, 7)) : events;
  const count = (k: EventKind) => inMonth.filter((e) => e.kind === k).length;
  const mine = inMonth.filter((e) => e.assigneeId === user?.id).length;
  const showPeople = canBook && (people.data?.length ?? 0) > 0;
  const showProperties = (properties.data?.length ?? 0) > 0;

  return (
    <div>
      <PageHeader
        title="Calendar"
        menu={[canBook && { label: 'Book an appointment', icon: 'Plus', onClick: () => setBooking(view === 'day' ? anchor : today) }, { label: 'Export CSV', export: { path: '/calendar', page: 'calendar', query: { from, to, staffId: staffId || undefined, kind: kind || undefined, propertyId: propertyId || undefined } } }]}
      />

      <div className="mb-2.5 hidden grid-cols-2 gap-2.5 sm:grid lg:grid-cols-4">
        <StatTile label={view === 'month' ? 'This month' : view === 'week' ? 'This week' : 'This day'} value={q.data ? String(inMonth.length) : '—'} hint="Everything in view" icon={<CalendarDays className="h-4 w-4" />} />
        <StatTile label="Work orders" value={q.data ? String(count('WORK_ORDER')) : '—'} hint="Jobs booked in" icon={<Wrench className="h-4 w-4" />} />
        <StatTile label="Viewings" value={q.data ? String(count('VIEWING')) : '—'} hint="Pipeline and booked by hand" icon={<Eye className="h-4 w-4" />} />
        <StatTile label="Mine" value={q.data ? String(mine) : '—'} hint="Assigned to you" icon={<UserRound className="h-4 w-4" />} />
      </div>

      <div className="surface mb-2.5 flex flex-wrap items-center gap-2 px-3 py-2">
        <div className="flex items-center gap-1">
          <button onClick={() => step(-1)} className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900" aria-label="Previous"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => step(1)} className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900" aria-label="Next"><ChevronRight className="h-4 w-4" /></button>
          <Button size="sm" variant="secondary" onClick={() => setAnchor(today)} className="ml-1">Today</Button>
        </div>
        <div className="min-w-0 flex-1 truncate px-1 text-[15px] font-semibold text-ink-900">{title}</div>
        {q.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
        <Segmented value={view} onChange={setView} items={(['month', 'week', 'day'] as const).map((v) => ({ key: v, label: v[0]!.toUpperCase() + v.slice(1) }))} />
        <div className="grid w-full grid-cols-2 gap-2 lg:flex lg:w-auto">
          {showPeople && (
            <Select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-full min-w-0 lg:w-44" aria-label="Whose diary">
              <option value="">Everyone</option>
              <option value="me">Mine</option>
              <option value="none">Nobody assigned</option>
              {people.data!.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
          <Select value={kind} onChange={(e) => setKind(e.target.value as EventKind | '')} className="w-full min-w-0 lg:w-40" aria-label="Kind">
            <option value="">Everything</option>
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </Select>
          {showProperties && (
            <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className={`w-full min-w-0 lg:w-48 ${showPeople ? 'col-span-2 lg:col-span-1' : ''}`} aria-label="Property">
              <option value="">Every property</option>
              {properties.data!.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
        </div>
      </div>

      <div className="-mx-4 mb-2.5 flex gap-1.5 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-1 sm:pb-0">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind((cur) => (cur === k.key ? '' : k.key))}
            className={`inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[12px] transition ${kind === k.key ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: k.colour }} />{k.label}
            {q.data && count(k.key) ? <span className="text-ink-400">{count(k.key)}</span> : null}
          </button>
        ))}
      </div>

      {q.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load the calendar" body={(q.error as Error).message} />
      ) : narrow ? (
        <Agenda from={from} to={to} byDay={byDay} today={today} onOpen={open} loading={q.isLoading} onBook={canBook ? setBooking : undefined} />
      ) : view === 'month' ? (
        <MonthGrid from={from} anchor={anchor} byDay={byDay} today={today} onOpen={open} onDay={(d) => { setAnchor(d); setView('day'); }} onBook={canBook ? setBooking : undefined} />
      ) : (
        <TimeGrid days={view === 'week' ? Array.from({ length: 7 }, (_, i) => addDays(from, i)) : [anchor]} byDay={byDay} today={today} onOpen={open}
          onDay={(d) => { setAnchor(d); setView('day'); }} onBook={canBook ? setBooking : undefined} />
      )}

      {(booking || editing) && (
        <BookDialog day={booking ?? today} event={editing} people={people.data ?? []}
          onClose={() => { setBooking(null); setEditing(null); }}
          onDone={() => { setBooking(null); setEditing(null); refresh(); }} />
      )}
      {viewingManual && <ReadOnly e={viewingManual} onClose={() => setViewingManual(null)} />}
    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function Chip({ e, onOpen, compact }: { e: CalEvent; onOpen: (e: CalEvent) => void; compact?: boolean }) {
  const c = colourOf(e.kind);
  return (
    <button onClick={(ev) => { ev.stopPropagation(); onOpen(e); }}
      title={[e.title, e.propertyName, e.unitRef, e.assignee].filter(Boolean).join(' · ')}
      className={`block w-full truncate rounded-md border-l-[3px] text-left text-ink-800 transition hover:brightness-95 ${compact ? 'px-1.5 py-[2px] text-[11px]' : 'px-2 py-1 text-[12px]'}`}
      style={{ borderColor: c, background: `${c}1a` }}>
      {!e.allDay && <span className="mr-1 tabular-nums text-ink-500">{timeOf(e.start)}</span>}
      {e.title}
    </button>
  );
}

function MonthGrid({ from, anchor, byDay, today, onOpen, onDay, onBook }: {
  from: string; anchor: string; byDay: Map<string, CalEvent[]>; today: string;
  onOpen: (e: CalEvent) => void; onDay: (d: string) => void; onBook?: (d: string) => void;
}) {
  const month = anchor.slice(0, 7);
  return (
    <div className="surface overflow-hidden">
      <div className="grid grid-cols-7 border-b border-ink-100 bg-ink-50/70">
        {DOW.map((d) => <div key={d} className="px-2 py-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 42 }, (_, i) => {
          const d = addDays(from, i);
          const list = byDay.get(d) ?? [];
          const out = d.slice(0, 7) !== month;
          return (
            <div key={d} className={`group relative min-h-[112px] border-b border-r border-ink-100 p-1.5 ${(i + 1) % 7 === 0 ? 'border-r-0' : ''} ${out ? 'bg-ink-50/50' : 'bg-white'}`}>
              <div className="mb-1 flex items-center justify-between">
                <button onClick={() => onDay(d)} className={`grid h-6 min-w-6 place-items-center rounded-full px-1 text-[12px] tabular-nums transition hover:bg-ink-100 ${d === today ? 'bg-bronze-500 font-semibold text-on-primary hover:bg-bronze-600' : out ? 'text-ink-300' : 'text-ink-700'}`}>
                  {Number(d.slice(8))}
                </button>
                {onBook && <button onClick={() => onBook(d)} title="Book something on this day" className="rounded-full px-1.5 text-[14px] leading-none text-ink-300 opacity-0 transition hover:bg-ink-100 hover:text-ink-700 group-hover:opacity-100">+</button>}
              </div>
              <div className="space-y-[3px]">
                {list.slice(0, 3).map((e) => <Chip key={`${e.id}-${d}`} e={e} onOpen={onOpen} compact />)}
                {list.length > 3 && <button onClick={() => onDay(d)} className="px-1.5 text-[11px] font-medium text-bronze-600 hover:underline">+{list.length - 3} more</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Lay out a day's timed events in side-by-side lanes where they overlap. */
function lanes(list: CalEvent[]): Array<{ e: CalEvent; lane: number; of: number }> {
  const sorted = [...list].sort((a, b) => a.start.localeCompare(b.start));
  const out: Array<{ e: CalEvent; lane: number; of: number }> = [];
  let cluster: Array<{ e: CalEvent; lane: number; of: number }> = [];
  let clusterEnd = '';
  const laneEnds: string[] = [];
  const close = () => { const n = Math.max(1, ...cluster.map((c) => c.lane + 1)); cluster.forEach((c) => { c.of = n; }); out.push(...cluster); cluster = []; laneEnds.length = 0; clusterEnd = ''; };
  for (const e of sorted) {
    if (cluster.length && e.start >= clusterEnd) close();
    let lane = laneEnds.findIndex((end) => end <= e.start);
    if (lane < 0) { lane = laneEnds.length; laneEnds.push(e.end); } else laneEnds[lane] = e.end;
    cluster.push({ e, lane, of: 1 });
    if (e.end > clusterEnd) clusterEnd = e.end;
  }
  if (cluster.length) close();
  return out;
}

function TimeGrid({ days, byDay, today, onOpen, onDay, onBook }: {
  days: string[]; byDay: Map<string, CalEvent[]>; today: string;
  onOpen: (e: CalEvent) => void; onDay: (d: string) => void; onBook?: (d: string) => void;
}) {
  const hours = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
  const cols = `56px repeat(${days.length}, minmax(0, 1fr))`;
  const top = (m: number) => ((Math.min(Math.max(m, DAY_START * 60), DAY_END * 60) - DAY_START * 60) / 60) * HOUR_PX;
  return (
    <div className="surface overflow-hidden">
      <div className="grid border-b border-ink-100 bg-ink-50/70" style={{ gridTemplateColumns: cols }}>
        <div />
        {days.map((d) => (
          <button key={d} onClick={() => onDay(d)} className="border-l border-ink-100 px-2 py-2 text-left">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{fmt(d, { weekday: 'short' })}</span>{' '}
            <span className={`ml-1 inline-grid h-6 min-w-6 place-items-center rounded-full px-1 text-[12.5px] tabular-nums ${d === today ? 'bg-bronze-500 font-semibold text-on-primary' : 'text-ink-800'}`}>{Number(d.slice(8))}</span>
          </button>
        ))}
      </div>
      {/* All-day row: certificates, servicing, move dates, inspections, all-day bookings */}
      <div className="grid border-b border-ink-100" style={{ gridTemplateColumns: cols }}>
        <div className="px-2 py-1.5 text-right text-[10px] uppercase tracking-[0.12em] text-ink-400">All day</div>
        {days.map((d) => (
          <div key={d} className="min-h-[36px] space-y-[3px] border-l border-ink-100 p-1">
            {(byDay.get(d) ?? []).filter((e) => e.allDay).map((e) => <Chip key={`${e.id}-${d}`} e={e} onOpen={onOpen} compact />)}
          </div>
        ))}
      </div>
      <div className="max-h-[640px] overflow-y-auto">
        <div className="relative grid" style={{ gridTemplateColumns: cols }}>
          <div>
            {hours.map((h) => <div key={h} className="pr-2 text-right text-[10.5px] tabular-nums text-ink-400" style={{ height: HOUR_PX }}>{String(h).padStart(2, '0')}:00</div>)}
          </div>
          {days.map((d) => (
            <div key={d} className={`relative border-l border-ink-100 ${d === today ? 'bg-bronze-50/30' : ''}`} style={{ height: hours.length * HOUR_PX }}
              onDoubleClick={() => onBook?.(d)}>
              {hours.map((h) => <div key={h} className="border-b border-ink-100/70" style={{ height: HOUR_PX }} />)}
              {lanes((byDay.get(d) ?? []).filter((e) => !e.allDay)).map(({ e, lane, of }) => {
                const startM = e.day === d ? minutesOf(e.start) : 0;
                const endM = e.day === d && new Date(e.end).getDate() === new Date(e.start).getDate() ? minutesOf(e.end) : 24 * 60;
                const y = top(startM);
                const h = Math.max(top(endM) - y, 22);
                const c = colourOf(e.kind);
                return (
                  <button key={e.id} onClick={() => onOpen(e)} title={[e.title, e.propertyName, e.unitRef, e.assignee].filter(Boolean).join(' · ')}
                    className="absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left text-[11.5px] leading-tight text-ink-800 shadow-sm transition hover:brightness-95"
                    style={{ top: y + 1, height: h - 2, left: `calc(${(lane / of) * 100}% + 2px)`, width: `calc(${100 / of}% - 4px)`, borderColor: c, background: `color-mix(in srgb, ${c} 14%, white)` }}>
                    {h < 40 ? (
                      <div className="truncate"><span className="tabular-nums text-ink-500">{timeOf(e.start)}</span> <span className="font-medium">{e.title}</span></div>
                    ) : (
                      <>
                        <div className="tabular-nums text-ink-500">{timeOf(e.start)}–{timeOf(e.end)}</div>
                        <div className="font-medium">{e.title}</div>
                        {h > 60 && <div className="truncate text-ink-500">{[e.unitRef ?? e.propertyName, e.assignee].filter(Boolean).join(' · ')}</div>}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The phone layout: one row per day with something in it. */
function Agenda({ from, to, byDay, today, onOpen, loading, onBook }: {
  from: string; to: string; byDay: Map<string, CalEvent[]>; today: string; onOpen: (e: CalEvent) => void; loading: boolean; onBook?: (d: string) => void;
}) {
  const days = [...byDay.keys()].filter((d) => d >= from && d <= to).sort();
  if (loading) return <div className="surface space-y-3 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-ink-100" />)}</div>;
  if (!days.length) return <EmptyState icon="CalendarDays" title="Nothing booked" body="Nothing in this period for these filters." />;
  return (
    <div className="space-y-3">
      {days.map((d) => (
        <section key={d} className="surface overflow-hidden">
          <div className={`flex items-center gap-3 border-b border-ink-100 px-4 py-2 ${d === today ? 'bg-bronze-50' : 'bg-ink-50/70'}`}>
            <span className={`text-[20px] font-semibold leading-none ${d === today ? 'text-bronze-600' : 'text-ink-900'}`}>{Number(d.slice(8))}</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">{fmt(d, { weekday: 'long', month: 'short' })}</span>
            <span className="flex-1" />
            {onBook && <button onClick={() => onBook(d)} className="rounded-full px-2 text-[16px] leading-none text-ink-400 hover:bg-ink-100 hover:text-ink-800" aria-label="Book on this day">+</button>}
          </div>
          <div className="divide-y divide-ink-100">
            {(byDay.get(d) ?? []).map((e) => {
              const c = colourOf(e.kind);
              return (
                <button key={`${e.id}-${d}`} onClick={() => onOpen(e)} className="flex w-full gap-3 px-4 py-3 text-left transition hover:bg-ink-50" style={{ boxShadow: `inset 3px 0 0 ${c}` }}>
                  <span className="w-[74px] shrink-0 pt-0.5 text-[11.5px] tabular-nums text-ink-500">{e.allDay ? 'All day' : `${timeOf(e.start)}–${timeOf(e.end)}`}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-ink-900">{e.title}</span>
                    <span className="block truncate text-[11.5px] text-ink-500">{[e.propertyName, e.unitRef, e.assignee].filter(Boolean).join(' · ') || e.detail}</span>
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10.5px] font-medium" style={{ background: `${c}1f`, color: c }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{KIND_BY_KEY[e.kind]?.label ?? e.kindLabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function ReadOnly({ e, onClose }: { e: CalEvent; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={e.title} footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <div className="space-y-2 text-[13px] text-ink-700">
        <div>{e.allDay ? (e.start === e.end ? fmt(e.start, { weekday: 'long', day: 'numeric', month: 'long' }) : `${fmt(e.start, { day: 'numeric', month: 'short' })} – ${fmt(e.end, { day: 'numeric', month: 'short' })}`) : `${fmt(e.day, { weekday: 'long', day: 'numeric', month: 'long' })}, ${timeOf(e.start)}–${timeOf(e.end)}`}</div>
        <div className="text-ink-500">{[KIND_BY_KEY[e.kind]?.label, e.propertyName, e.unitRef].filter(Boolean).join(' · ')}</div>
      </div>
    </Modal>
  );
}

// ─── Book / edit ─────────────────────────────────────────────────────────────

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 480];
const durLabel = (m: number) => (m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} hour${m === 60 ? '' : 's'}`);
const localTime = (iso: string) => timeOf(iso);
const localDay = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function BookDialog({ day, event, people, onClose, onDone }: { day: string; event: CalEvent | null; people: Array<{ id: string; name: string; roleName: string | null }>; onClose: () => void; onDone: () => void }) {
  const { user, can } = useAuth();
  const editing = !!event;
  const initialMinutes = event && !event.allDay ? Math.round((Date.parse(event.end) - Date.parse(event.start)) / 60_000) : 60;
  const [kind, setKind] = useState<ManualKind>((event?.kind as ManualKind) ?? 'MEETING');
  const [title, setTitle] = useState(event?.title ?? '');
  const [allDay, setAllDay] = useState(event?.allDay ?? false);
  const [date, setDate] = useState(event ? (event.allDay ? event.start : localDay(event.start)) : day);
  const [endDate, setEndDate] = useState(event?.allDay ? event.end : '');
  const [time, setTime] = useState(event && !event.allDay ? localTime(event.start) : '10:00');
  const [minutes, setMinutes] = useState(initialMinutes);
  const [propertyId, setPropertyId] = useState(event?.propertyId ?? '');
  const [unitId, setUnitId] = useState(event?.unitId ?? '');
  const [assignedTo, setAssignedTo] = useState(event ? (event.assigneeId ?? '') : (people.some((p) => p.id === user?.id) ? user!.id : ''));
  const [notes, setNotes] = useState(event?.notes ?? '');
  const properties = usePropertyOptions();
  const units = useUnitOptions({ propertyId }, !!propertyId);
  const durations = DURATIONS.includes(minutes) ? DURATIONS : [...DURATIONS, minutes].sort((a, b) => a - b);

  const body = () => ({
    kind, title: title.trim(), allDay, tz: browserTz(),
    startsAt: allDay ? date : `${date}T${time}`,
    endsAt: allDay ? (endDate && endDate > date ? endDate : date) : null,
    durationMinutes: allDay ? null : minutes,
    propertyId: propertyId || null, unitId: unitId || null, assignedTo: assignedTo || null, notes: notes.trim() || null,
  });
  const save = useMutation({ mutationFn: () => (event ? calendarApi.update(event.id, body()) : calendarApi.book(body())), onSuccess: onDone });
  const cancel = useMutation({ mutationFn: () => calendarApi.cancel(event!.id), onSuccess: onDone });
  const [confirming, setConfirming] = useState(false);
  const ready = title.trim().length > 0 && !!date && (allDay || !!time);

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit booking' : 'Book something in'} size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {editing && can('calendar.delete') && (confirming
            ? <><span className="text-[12.5px] text-ink-600">Take it off the calendar?</span><Button variant="danger" size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>{cancel.isPending ? 'Cancelling…' : 'Yes, cancel it'}</Button><Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>No</Button></>
            : <Button variant="ghost" size="sm" icon="Trash2" onClick={() => setConfirming(true)} className="!text-red-600">Cancel booking</Button>)}
          <span className="flex-1" />
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>{save.isPending ? 'Saving…' : editing ? 'Save' : 'Book it'}</Button>
        </div>
      }>
      <p className="mb-2.5 text-[12.5px] leading-relaxed text-ink-500">For anything with no record of its own — a viewing, a residents’ meeting, a site visit, a contractor coming to quote. Work orders, inspections and tenancy dates put themselves on the calendar.</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="What kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value as ManualKind)} className="w-full">
            {KINDS.filter((k) => k.manual).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </Select>
        </Field>
        <Field label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="What is it"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Roof survey — Raffles House" className="w-full" /></Field>
        </div>
        <label className="flex items-center gap-2 text-[13px] text-ink-700 sm:col-span-2">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-4 w-4 accent-bronze-500" /> All day
        </label>
        {allDay ? (
          <Field label="Until (optional)" hint="The last day, for something that runs over several days.">
            <Input type="date" value={endDate} min={date} onChange={(e) => setEndDate(e.target.value)} className="w-full" />
          </Field>
        ) : (
          <>
            <Field label="Starts at"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full" /></Field>
            <Field label="How long">
              <Select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="w-full">
                {durations.map((m) => <option key={m} value={m}>{durLabel(m)}</option>)}
              </Select>
            </Field>
          </>
        )}
        <Field label="Property">
          <Select value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setUnitId(''); }} className="w-full">
            <option value="">None</option>
            {(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="Unit">
          <Select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!propertyId} className="w-full">
            <option value="">The whole building</option>
            {(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.reference}</option>)}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Whose diary">
            <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="w-full">
              <option value="">Nobody in particular</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.roleName ? ` · ${p.roleName}` : ''}</option>)}
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Who is coming, access, what to bring…" className="w-full" /></Field>
        </div>
      </div>
      <div className="mt-3"><FormError error={save.error ?? cancel.error} /></div>
    </Modal>
  );
}
